/**
 * IMP::: Dealtable Actions is for all types for CRUD actions that are
 * done in dealtable page, which includes both grid and product catalog components.
 * The support includes multiple dealtable as well.
 */

/**
 * * INDEX
 * - getInitData
 * - getDealTableRows
 * - getCatalogData
 * - updateProductCatalogs
 * - addProductsToDealTable
 * - onCellValueChange
 * - onRowDelete
 * - onDealTableSave
 * - onGroupDiscountUpdate
 * - onDealDiscountUpdate
 * - onGroupApply
 * - onGroupDelete
 * - netPriceRounding
 * - applySorting
 */

// NPM Imports
import { cloneDeep, pick } from "lodash";

// Internal Imports
import * as AgGrid from "../../mashupV2";
import * as AgGridV4 from "../../mashupV4";
import store from "../../store";
import { COMMON, GRID } from "../../constants";
import { DealTableUtils, GlobalUtils } from "../../utils";

// Service Imports
import {
  createDealTableGroup,
  fetchDealTableColumns,
  fetchDealTableRows,
  fetchProductCatalogs,
  removeAllDealTableGroups,
  removeDealTableRow,
  saveDealTableRow,
  updateDealTableGroup,
  updateOverallDealDiscount,
  removeDealTableGroup,
} from "./DealTableServices";

// Method Imports
import {
  addGroupIDIntoRow,
  generateDiscountData,
  // generateGroupData,
  // removeGroupIDFromRow,
  handleNetPriceRounding,
  setFlagsToCatalogItems,
  updateOverallDiscount,
  updateProductCatalogFlags,
  generateGroupDataInDealTable,
} from "./DealTableMethods";
import { updateDocSaveIndicator } from "../DocumentActions/method";

// Destructuring
const { CURRENCY_SYMBOL } = COMMON;
const { GRID_PAGE_TYPE, DEALTABLE_COLUMNS } = GRID;
const {
  // eslint-disable-next-line no-unused-vars
  handlePercentageOfTotal,
  // mapProductToDealTable,
  // mapReactionsOnCellChange,
} = DealTableUtils;
const { getGridAPi, readDataFromLocalStorage } = GlobalUtils;

/**
 * "getInitData" func fetches dealtable column & row data.
 * @param {number} DealTableId - Refers to the dealtable id to be fetched // 15
 */
export const getInitData = DealTableId => async dispatch => {
  try {
    /**
     * The following steps have to done in sequence, for this flow.
     * 1) Fetch the dealtable View ID from the cookie, this is required to fetch the column & row data, and groups
     *    information from the document store.
     * 2) Make API calls to fetch DealTable Column & Row Data.
     * 3) Fetch & Declare const & let that are needed for this process.
     * 4) The columns JSON has to be restructured to AgGrid JSON structure.
     * 5) If any row(s) is saved, then restructure the JSON required by AgGrid.
     * 6) Generate footer data having the row count & discount(deal/group) which has to be displayed in UI.
     * 7) Finally create a payload to update the respective redux keys
     */

    // * STEP 1:
    const getAppView = readDataFromLocalStorage("appView");
    const { DealTableView } =
      getAppView && getAppView.views ? getAppView.views : {};
    const {
      document: {
        dealTable: groups = [],
        pricelist: { currency },
      },
    } = store.getState().DocumentStore;

    // * STEP 2:
    const dealTableColumns = fetchDealTableColumns(DealTableView);
    const dealTableRows = fetchDealTableRows(DealTableId, DealTableView);

    await Promise.all([dealTableColumns, dealTableRows]).then(response => {
      // * STEP 3:
      const dealTableColumnsData = response[0];
      const dealTableRowsData = response[1];
      const { rowData = [] } = dealTableRowsData && dealTableRowsData.ViewData;
      let columnData = null;
      let discountData = {};
      let parsedRowData = {};

      // * STEP 4:
      columnData = AgGrid.formatColumnData(
        dealTableColumnsData,
        GRID_PAGE_TYPE.DEALTABLE,
        CURRENCY_SYMBOL[currency],
      );

      // * STEP 5:
      if (rowData && rowData.length > 0) {
        parsedRowData = AgGrid.formatRowData(
          rowData,
          columnData.metaData,
          GRID_PAGE_TYPE.DEALTABLE,
        );
      }

      // * STEP 6:

      discountData = generateDiscountData(
        groups,
        parsedRowData.rowData,
        columnData.metaData.fieldMapIndex,
      );

      // * STEP 7:
      const payload = {
        addedCatalogIds: parsedRowData.addedCatalogIds,
        columnDefs: columnData.columnDefs,
        customColumnDefs: columnData.customColumnDefs,
        dealTableId: DealTableId,
        discount: discountData,
        metaData: columnData.metaData,
        props: columnData.props,
        rowData: parsedRowData.rowData,
      };
      dispatch({
        type: "REACTION_ON_PAGE_LOAD_IN_DEALTABLE",
        payload,
      });
    });
  } catch (error) {
    console.error("DealTableActions => getInitData", error);
    throw error;
  }
};

/**
 * "getDealTableRows" func fetches row data for a particular dealtable id.
 * @param {number} DealTableId - Refers to the dealtable id to be fetched // 15
 */
export const getDealTableRows = (DealTableId, metaData) => async dispatch => {
  try {
    /**
     * *IMP::
     * For multi dealtable concept, the "addedCatalogIds, rowData" values are stored
     * as object with keys pointing to its dealtable id. Hence we will append these new
     * values inside the existing object.
     * 
     * 
     * The following steps have to done in sequence, for this flow.
     * 1) Fetch "discount", "fieldMapIndex" from store, then clone "discount"
           newly fetched data has to be added to the exising data. 
     * 2) Fetch the dealtable View ID from the cookie, this is required to fetch the row data.
     * 3) Make API calls to fetch DealTable Row Data.
     * 4) Declare const & let that are needed for the mutation process.
     * 5) The rows JSON has to be restructured to AgGrid JSON structure.
     * 6) Generate footer data having the row count & discount(deal/group) which has to be displayed in UI.
     * 7) Finally create a payload to update the respective redux keys
     */

    // * STEP 1:
    const {
      discount: discountObj,
      metaData: { fieldMapIndex },
    } = store.getState().DealTableStore;
    const discountClone = cloneDeep(discountObj[DealTableId]) || [];

    // * STEP 2:
    const getAppView = readDataFromLocalStorage("appView");
    const { DealTableView } =
      getAppView && getAppView.views ? getAppView.views : {};

    // * STEP 3:
    const dealTableRows = await fetchDealTableRows(DealTableId, DealTableView);

    await Promise.all([dealTableRows]).then(response => {
      // * STEP 4:
      const dealTableRowsData = response[0];
      const { rowData = [] } = dealTableRowsData && dealTableRowsData.ViewData;
      let parsedRowData = {};
      let discountData = {};

      // * STEP 5:
      if (rowData && rowData.length > 0) {
        parsedRowData = AgGrid.formatRowData(
          rowData,
          metaData,
          GRID_PAGE_TYPE.DEALTABLE,
        );

        // * STEP 6:
        discountData = generateDiscountData(
          discountClone,
          parsedRowData.rowData,
          fieldMapIndex,
        );

        // * STEP 7:
        const payload = {
          addedCatalogIds: parsedRowData.addedCatalogIds,
          dealTableId: DealTableId,
          discount: discountData,
          rowData: parsedRowData.rowData,
        };
        dispatch({
          type: "UPDATE_DEALTABLE_ROWS",
          payload,
        });
      }
    });
  } catch (error) {
    console.error("DealTableActions => getDealTableRows", error);
    throw error;
  }
};

/**
 * "getCatalogData" func searches catalog products based on pricelist, searchQuery & productType.
 * @param {number} pricelistID - Refers to the pricelist id to be fetched // 15
 * @param {string} searchQuery - Search parameter the user enters, likely the product name.
 * @param {string} productType - Refers to different product types categorized, say (Resources, Hardware, License, etc.,)
 * @param {array} addedCatalogIds - Holds product id's which are already added to a particular dealtable by dealtable id.
 */
export const getCatalogData = (
  pricelistID,
  searchQuery,
  productType,
  addedCatalogIds,
) => async dispatch => {
  try {
    const productCatalogList = fetchProductCatalogs(
      pricelistID,
      searchQuery,
      productType,
    );

    await Promise.all([productCatalogList])
      .then(response => {
        dispatch({
          type: "UPDATE_PRODUCT_CATALOG_LIST",
          payload: setFlagsToCatalogItems(
            response[0].PriceListItems,
            addedCatalogIds,
          ),
        });
        return response[0].PriceListItems;
      })
      .catch(error => {
        console.error("DealTableActions => getCatalogData", error);
        throw error;
      });
  } catch (error) {
    throw error;
  }
};

/**
 * "updateProductCatalogs" func updates the isChecked/isDisabled flag of a single/multiple product data.
 * @param {number} productId(s) - Points to the product id // 15
 * @param {boolean} flag - Holds true/false value to set in flagType.
 * @param {string} flagType - Key which the flag value gets added to, like "isChecked & isDisabled".
 * @param {arrayOfObj} productCatalogs - Holds the entire product catalog list.
 */
export const updateProductCatalogs = (
  productIds,
  flag,
  flagType,
  productCatalogs,
) => dispatch => {
  dispatch({
    type: "UPDATE_PRODUCT_CATALOG_LIST",
    payload: updateProductCatalogFlags(
      productIds,
      flag,
      flagType,
      productCatalogs,
    ),
  });
};

/**
 * "addProductsToDealTable" func adds products from catalog search into the dealtable.
 * @param {number} productId(s) - Points to the product id // 15
 * @param {boolean} flag - Holds true/false value to set in flagType.
 * @param {string} flagType - Key which the flag value gets added to, like "isChecked & isDisabled".
 * @param {arrayOfObj} productCatalogs - Holds the entire product catalog list.
 */
export const addProductsToDealTable = (
  productIds,
  flag,
  flagType,
  productCatalogs,
  dealTableId,
) => dispatch => {
  const {
    addedCatalogIds,
    discount,
    rowData,
    metaData,
  } = store.getState().DealTableStore;

  /**
   * * Instead of deep cloning the whole data object, pick only the data which belongs
   * * to the dealtable and then do the mutation on top. Here in reducer only the required
   * * dealtable data gets updated.
   */
  const addedCatalogIdsClone = [...addedCatalogIds[dealTableId]] || [];
  const discountClone = cloneDeep(discount[dealTableId]) || [];
  const rowDataClone = rowData[dealTableId].map(row => row) || [];

  return AgGridV4.reactionsOnAddingProductsToDealTable(
    productIds,
    flag,
    flagType,
    productCatalogs,
    metaData,
    dealTableId,
    // Props To Be Updated After Row Data Is Created
    addedCatalogIdsClone,
    discountClone,
    rowDataClone,
  ).then(gridData => {
    dispatch({
      type: "REACTION_ON_ADDING_ROWS_INTO_DEALTABLE",
      payload: {
        addedCatalogIds: gridData.catalogIds,
        dealTableId,
        discount: gridData.discount,
        productCatalogs: gridData.productCatalogs,
        rowData: gridData.rowData,
      },
    });
    return "Success";
  });
};

export const addProductsToDealTableV2 = (
  productIds,
  flag,
  flagType,
  productCatalogs,
  dealTableId,
  pricelistId,
  documentId,
) => dispatch => {
  try {
    const { discount, metaData, rowData } = store.getState().DealTableStore;

    /**
     * * Instead of deep cloning the whole data object, pick only the data which belongs
     * * to the dealtable and then do the mutation on top. Here in reducer only the required
     * * dealtable data gets updated.
     */
    const discountClone = cloneDeep(discount[dealTableId]) || [];
    const rowDataClone = rowData[dealTableId].map(row => row) || [];
    dispatch(updateDocSaveIndicator("SAVING"));
    return AgGridV4.reactionsOnAddingProductsToDealTableV2(
      productIds,
      flag,
      flagType,
      productCatalogs,
      metaData,
      dealTableId,
      pricelistId,
      documentId,
      // Props To Be Updated After Row Data Is Created
      discountClone,
      rowDataClone,
    )
      .then(gridData => {
        dispatch(updateDocSaveIndicator("SAVED"));
        dispatch({
          type: "REACTION_ON_ADDING_ROWS_INTO_DEALTABLE",
          payload: {
            addedCatalogIds: gridData.catalogIds,
            dealTableId,
            discount: gridData.discount,
            productCatalogs: gridData.productCatalogs,
            rowData: gridData.rowData,
          },
        });
        return "Success";
      })
      .catch(error => {
        dispatch(updateDocSaveIndicator("SAVE_FAILED"));
        console.error("DealTableActions => addProductsToDealTableV2", error);
        throw error;
      });
  } catch (error) {
    console.error("DealTableActions => onRowDelete", error);
    throw error;
  }
};

/**
 * "onCellValueChange" func is a callback when any of the cell is edited, this is done to pass the
 * value from the view(UI) to the redux.
 * @param {string} fieldName - Holds the column field name
 * @param {Object} row - Holds the data of only the mutated row in the grid.
 */
export const onCellValueChange = (
  columnData,
  row,
  rowIndex,
  rowData,
  dealTableId,
) => dispatch => {
  /**
   * To update the new row data into redux, the following steps have to done in sequence.
   * 1) Mark the row as "dirty", this is done to filter only the mutated rows and make API(For Performance).
   * 2) When a cell value changes, it may have a ripple effect, so all the effect have to be done and updated in redux.
   *     Like, when QTY changes the Extended Base Price have to be updated, and so onn. After that a parser is ran to do the
   *     respective business actions.
   * 3) Create a payload to update the row data in redux.
   * 4) Finally call ag-grid refresh api to updated the UI, this is done coz redux will hold the new data but grid UI shows the old value
   *    due to deltaRowMode feature.
   */

  const { discount: discountObj, metaData } = store.getState().DealTableStore;
  const rowDataClone = rowData.map(items => items);
  const discountClone = cloneDeep(discountObj[dealTableId]) || [];

  // * STEP 1:
  rowDataClone[rowIndex].rowMeta.isDirty = true;

  // * STEP 2:
  return AgGridV4.reactionsOnCellChange(
    columnData,
    row,
    rowIndex,
    rowDataClone,
    metaData,
    discountClone,
    dealTableId,
  ).then(updatedData => {
    dispatch({
      type: "REACTION_ON_CELLVALUE_CHANGE_IN_DEALTABLE",
      payload: {
        dealTableId,
        discount: updatedData.discount,
        rowData: updatedData.rowData,
      },
    });
    return "Success";
  });
};

/**
 * "onDealDiscountUpdate" func is responsible for updating any dealtable level discounts.
 */
export const onDealDiscountUpdate = (
  discountValue,
  discountObj,
  dealTableId,
) => async dispatch => {
  const discountData = cloneDeep(discountObj);
  if (!discountData.dealTotal) discountData.dealTotal = 0;
  const discountAmount = (discountData.dealTotal * (discountValue || 0)) / 100;
  discountData.deal_discount = discountValue || 0;
  discountData.discountAmount = discountAmount;
  discountData.discountedDealTotal = discountData.dealTotal - discountAmount;

  // Api call for discount update..
  // Here deal_discount : stands for discount percent ,
  // total_discount : stands for after calculating the percentage
  // total_price : stands for after calculating of the percentage & deducted from netPrice
  // net_price : stands for calculating if the all row net price details
  const dealDiscountPayload = {
    deal_discount: parseFloat(discountValue) || 0,
    // total_discount: discountData.discountAmount,
    total_price: discountData.discountedDealTotal,
    net_price: discountObj.dealTotal,
  };
  const updateDealDiscountValue = await updateOverallDealDiscount(
    dealTableId,
    dealDiscountPayload,
  );
  const discount = updateDealDiscountValue.Deal;
  discount.dealTotal = discount.net_price;
  discount.discountAmount = (discount.dealTotal * (discountValue || 0)) / 100;
  discount.deal_discount = discountValue || 0;
  discount.discountedDealTotal = discount.dealTotal - discount.discountAmount;
  discount.groups = discountObj.groups;

  dispatch({
    type: "REACTION_ON_DISCOUNT_UPDATE_IN_DEALTABLE",
    payload: { dealTableId, discount },
  });
};

export const onRowDelete = (
  rowMeta,
  flag,
  dealTableId,
  groupMeta,
  groupLength,
  groupReference,
) => async dispatch => {
  try {
    // * STEP 1:
    const {
      addedCatalogIds,
      discount: discountObj,
      metaData,
      productCatalogs,
      rowData,
    } = store.getState().DealTableStore;
    const {
      masterTblID,
      tableId,
      fieldMapIndex,
      apiRequiredFields,
      tblViewId,
    } = metaData;

    const rowDataClone = rowData[dealTableId].map(item => item);
    const discountClone = cloneDeep(discountObj[dealTableId]) || [];

    console.debug("discountClone", groupMeta, discountClone);
    let groupDelete = null;
    // * STEP 2:
    let rowDelete = null;
    if (rowMeta.id) {
      rowDelete = await removeDealTableRow(
        masterTblID,
        dealTableId,
        tableId,
        rowMeta.id,
      );
    }

    // * STEP 3:
    const updatedAddedCatalogIds = addedCatalogIds[dealTableId].filter(
      item => item !== rowMeta.index,
    );
    const updatedRowData = rowDataClone.filter(
      item => item.rowMeta.index !== rowMeta.index,
    );
    const updatedProductCatalogs = updateProductCatalogFlags(
      [rowMeta.index],
      flag,
      null,
      productCatalogs,
    );

    // * STEP 4 : POT Calculation:
    const updatedRowInfo = handlePercentageOfTotal(
      updatedRowData,
      fieldMapIndex,
    );

    // * Update backend for POT product
    const potProduct = updatedRowInfo.filter(
      row =>
        row.rowMeta.pricingData.type ===
        GRID.PRICING_METHODS.PERCENTAGE_OF_TOTAL,
    );
    if (potProduct.length > 0) {
      let potProductIds = [];
      potProduct.forEach(product => {
        //* Filter out the product Ids which are mapped to percentageOfTotalProduct
        potProductIds =
          product.rowMeta.pricingData.priceListItems &&
          product.rowMeta.pricingData.priceListItems.map(
            item => item.pricelist_item_id,
          );
      });

      if (
        potProductIds &&
        potProductIds.length > 0 &&
        potProductIds.includes(rowMeta.index)
      ) {
        const apiPayload = potProduct.map(row => {
          const obj = {};
          if (row.rowMeta.id) obj.id = row.rowMeta.id;
          Object.keys(row.rowData).forEach(fld => {
            if (apiRequiredFields.includes(fld))
              obj[fld] = row.rowData[fld].cellData;
          });
          return obj;
        });

        saveDealTableRow(
          dealTableId,
          apiPayload,
          masterTblID,
          tableId,
          tblViewId,
        );
      }
    }

    const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];

    // STEP 5 : NETPRICE ROUNDING:

    const updatedRowDataWithPOT = handleNetPriceRounding(
      updatedRowInfo,
      netPriceFld,
    );

    // * STEP 6:
    let dealTotal = 0;
    if (groupReference && discountClone.groups.length > 0) {
      if (groupLength === 1) {
        let updatedGroupData = [];

        updatedGroupData = discountClone.groups.filter(
          item => item.group_by_value !== groupMeta.group_by_value,
        );

        groupDelete = await removeDealTableGroup(dealTableId, groupMeta.id);
        discountClone.groups = updatedGroupData;
      }

      const groupDetailArray = [];
      const groupData = updatedRowDataWithPOT.reduce((r, a) => {
        r[a.rowData[groupReference].cellData] =
          r[a.rowData[groupReference].cellData] || [];
        r[a.rowData[groupReference].cellData].push(a);
        return r;
      }, Object.create(null));
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(groupData)) {
        groupDetailArray.push({
          groupName: key,
          groupDataDetails: value,
        });
      }

      if (groupDetailArray.length > 0) {
        groupDetailArray.forEach(groupInfo => {
          const netPriceArray = [];
          groupInfo.groupDataDetails.forEach(eachData => {
            netPriceArray.push(eachData.rowData[netPriceFld].cellData);
            const sumOfRows = netPriceArray.reduce((a, b) => a + b, 0);
            // eslint-disable-next-line no-shadow
            const groupData = discountClone.groups.find(
              group => group.group_by_value === groupInfo.groupName,
            );
            const discountAmount =
              (sumOfRows * ((groupData && groupData.discount_percent) || 0)) /
              100;
            groupData.group_total = sumOfRows;
            groupData.discountAmount = discountAmount;
            groupData.discountedGroupTotal = sumOfRows - discountAmount;
          });
        });
        discountClone.dealTotal = discountClone.groups
          .map(group => group.discountedGroupTotal)
          .reduce((a, b) => a + b, 0);
      }
      // Group Data Update Call
      const updatedGroupsPayload = { groups: discountClone.groups };
      updateDealTableGroup(dealTableId, updatedGroupsPayload);
    } else {
      const netPrices = updatedRowDataWithPOT.map(
        row => row.rowData[netPriceFld].cellData,
      );
      dealTotal = netPrices.reduce((a, b) => a + b, 0);
      discountClone.dealTotal = dealTotal;
    }

    if (updatedRowDataWithPOT.length === 0) {
      discountClone.deal_discount = 0;
      discountClone.dealTotal = 0;
    }
    // Code to check if a deleted row removes a group as a reaction
    store.dispatch(
      onDealDiscountUpdate(
        discountClone.deal_discount,
        discountClone,
        dealTableId,
      ),
    );
    await Promise.all([rowDelete, groupDelete])
      .then(response => {
        // * STEP 7:
        dispatch({
          type: "REACTION_ON_ROW_DELETE_IN_DEALTABLE",
          payload: {
            addedCatalogIds: updatedAddedCatalogIds,
            // discount: discountClone,
            dealTableId,
            productCatalogs: updatedProductCatalogs,
            rowData: updatedRowDataWithPOT,
          },
        });
        return response;
      })
      .catch(error => {
        console.error("DealTableActions => onRowDelete", error);
        throw error;
      });
  } catch (error) {
    console.error("DealTableActions => onRowDelete", error);
    throw error;
  }
};

/**
 * "onDealTableSave" func saves the row(s), group discount & deal discount to BackEnd.
 */
export const onDealTableSave = () => async dispatch => {
  try {
    /**
     * The following steps have to done in sequence, for this flow.
     * 1) Fetch & Declare all the data that are needed for this process.
     * 2) Filter out the dirty row(s) from the whole list, here "filteredRows" key will be overridden to store the entire
     *    row data in case of when the user just group the table and makes no changes in any of the row in dealtable.
     * 3) Check if any grouping logic is applied, if yes, then do the following
     *   3.1) Filter out the newly created groups & the mutated groups
     *    - NEWLY CREATED GROUPS - Adding a new product into dealtable, which can have a group type which doesn't exists inside the dealtable.
     *    - MUTATED GROUPS - A group already exists inside the dealtable, not any discount value is changed then its considered as mutated
     *      and its has to be updated in backend as well.
     *   3.2) If the gouping logic is applied, but the rows are untuched( not dirty rows), then replace "filteredRows" key with entire rows, this
     *       is need coz, once the group is created BackEnd will send an ID for each group, now this groupId has to be added into the row. Doing so
     *       we will know this row is mapped to this specific group.
     *   3.3) Make an API to save the newly added groups in the backend.
     *   3.4) After saving the new group(s) to BackEnd, take out the groupId and add the group id's into the row(s).
     *   3.5) If any the existing goup(s) discount is changed, then update the new value to the backend via API.
     * 4) Check if any deal level discount is added, here the default value will be 0.
     *   !4.1) If the value is mutated then replace "filteredRows" key with entire rows, this is done coz there is a column called "overallDiscountPercentFld",
     *       here the overall discount percent is splitted by doing a math (REFER: "updateOverallDiscount" func) then the result is inserted into each row.
     *       This is done, coz backend can trigger workflow if the condition is over ruled.
     *   4.1) Make an API to update the deal level discount in the backend.
     * 5) Once the deal level discount value is updated in backend, the rows have to be updated. (REFER STEP: 4.1)
     * 6) Pick out the keys need for the backend, only the keys of the same table in DB.
     * 7) Make API to update the row(s).
     * 8) The rows JSON has to be restructured to AgGrid JSON structure.
     * 9) Generate footer data having the row count & discount(deal/group) which has to be displayed in UI.
     * 10) Finally create a payload to update the "discount" & "rowData" values in redux.
     */

    // * STEP 1:
    const {
      metaData,
      metaData: {
        apiRequiredFields,
        fieldMapIndex,
        masterTblID,
        tableId,
        tblViewId,
      },
      rowData: rowDataObj,
      discount: discountObj,
    } = store.getState().DealTableStore;
    const { dealTableId } = store.getState().QuotationStore;

    const discountClone = cloneDeep(discountObj[dealTableId]) || [];
    const {
      groups = [],
      isDealDiscountDirty,
      deal_discount: dealDiscount,
      discountedDealTotal,
    } = discountClone;
    const filteredFields = [];
    const rowData = rowDataObj[dealTableId];
    let savedRows = null;

    // * STEP 2:
    let filteredRows = rowData.filter(row => row.isDirty);

    // * STEP 3:
    let addNewGroups = null;
    let updateExistingGroups = null;
    const api = getGridAPi();
    const isGroupingApplied = api && api.columnApi.getRowGroupColumns();

    if (isGroupingApplied && isGroupingApplied.length > 0 && groups) {
      // * STEP 3.1:

      // To convert string to float value
      groups.forEach(group => {
        group.discount_percent = parseFloat(group.discount_percent);
      });

      const newlyCreatedGroups = groups.filter(group => !group.id);
      const mutatedExistingGroups = groups.filter(
        group => group.id && group.isDirty,
      );

      if (newlyCreatedGroups && newlyCreatedGroups.length > 0) {
        // * STEP 3.2:
        if (filteredRows.length === 0) filteredRows = rowData;

        // * STEP 3.3:
        const newlyCreatedGroupsPayload = { groups: newlyCreatedGroups };
        addNewGroups = await createDealTableGroup(
          dealTableId,
          newlyCreatedGroupsPayload,
        );

        // * STEP 3.4:
        addGroupIDIntoRow(
          addNewGroups.Deal.groups,
          isGroupingApplied[0].colDef.field,
          filteredRows,
          fieldMapIndex,
        );
      }

      // * STEP 3.5:
      if (mutatedExistingGroups && mutatedExistingGroups.length > 0) {
        const updatedGroupsPayload = { groups: mutatedExistingGroups };
        updateExistingGroups = await updateDealTableGroup(
          dealTableId,
          updatedGroupsPayload,
        );
      }
    }

    // * STEP 4:
    let updateDealDiscountValue = null;
    if (isDealDiscountDirty || filteredRows.length > 0) {
      filteredRows = rowData;

      // * STEP 4.1:
      const extendedBasePriceFieldId =
        fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE];
      const extendedBasePrice = rowData.map(
        row => row[extendedBasePriceFieldId],
      );
      const totalPrice = extendedBasePrice.reduce((a, b) => a + b, 0);
      const dealDiscountPayload = {
        deal_discount: parseFloat(dealDiscount),
        total_price: totalPrice,
        net_price: discountedDealTotal,
      };
      updateDealDiscountValue = await updateOverallDealDiscount(
        dealTableId,
        dealDiscountPayload,
      );
    }

    if (filteredRows.length > 0) {
      // * STEP 5:
      updateOverallDiscount(
        filteredRows,
        discountClone,
        fieldMapIndex,
        DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT,
        DEALTABLE_COLUMNS.DISC_PERCENT,
        DEALTABLE_COLUMNS.OVERALL_DISCOUNT_PERCENT,
      );

      // * STEP 6:
      filteredRows.forEach(row => {
        filteredFields.push(pick(row, apiRequiredFields));
      });

      // * STEP 7:
      savedRows = await saveDealTableRow(
        dealTableId,
        filteredRows,
        masterTblID,
        tableId,
        tblViewId,
      );
    }

    await Promise.all([
      addNewGroups,
      updateExistingGroups,
      updateDealDiscountValue,
      savedRows,
    ])
      .then(response => {
        // * STEP 8:
        const rows = response[3] && response[3].RowRecords;
        let parsedRowData = null;
        if (rows) {
          parsedRowData = AgGrid.formatRowData(
            rows,
            metaData,
            GRID_PAGE_TYPE.DEALTABLE,
          );
        }

        // * STEP 9:
        const discountData = generateDiscountData(
          (response[2] && response[2].Deal) || discountClone, // If groups are not added, then use the exising group data.
          rowData,
          fieldMapIndex,
          true,
        );

        // * STEP 10:
        dispatch({
          type: "REACTION_ON_DEALTABLE_SAVE",
          payload: {
            dealTableId,
            discount: discountData,
            rowData: (parsedRowData && parsedRowData.rowData) || rowData, // If only discount changes are done, then use the exising row data.
          },
        });
        return response;
      })
      .catch(error => {
        console.error("DealTableActions => onDealTableSave", error);
        throw error;
      });
  } catch (error) {
    console.error("DealTableActions => onDealTableSave", error);
    throw error;
  }
};

/**
 * "onGroupDiscountUpdate" func is responsible for updating any group level discounts.
 */
export const onGroupDiscountUpdate = (
  rowData,
  discount,
  netPriceFld,
  dealTableId,
  groupByValue,
  groupDiscountValue,
) => () => {
  const discountClone = cloneDeep(discount);
  const { groups } = discountClone;

  console.debug(
    "onGroupDiscountUpdate",
    groupByValue,
    groupDiscountValue,
    groups,
    netPriceFld,
    rowData,
  );

  const groupData = groups.find(group => group.group_by_value === groupByValue);
  const discountAmount =
    (groupData.group_total * (groupDiscountValue || 0)) / 100;
  groupData.discount_percent = groupDiscountValue || 0;
  groupData.discountAmount = discountAmount;
  groupData.discountedGroupTotal = groupData.group_total - discountAmount;
  // Api call for update the backend with discount percent & as well as particular group update - PUT call
  groups.forEach(group => {
    if (group.id === groupData.id) {
      group = groupData;
    }
  });
  const newlyCreatedGroupsPayload = { groups };
  updateDealTableGroup(dealTableId, newlyCreatedGroupsPayload);

  // calculation of overall discount
  discountClone.dealTotal = groups
    .map(group => group.discountedGroupTotal)
    .reduce((a, b) => a + b, 0);
  store.dispatch(
    onDealDiscountUpdate(
      discountClone.deal_discount,
      discountClone,
      dealTableId,
    ),
  );
};

/**
 * "onGroupApply" func adds group related info inside the  "discount" key in redux.
 * @param {arrayOfObj} rowData - Holds the current row(s) information in dealtable.
 * @param {Object} discount - Holds the discount related information of the selected dealtable.
 * @param {String} columnName - Holds the column name which determines the groupBy logic.
 * @param {Object} fieldMapIndex - Holds the column name => FLDXXX map.
 * @param {number} dealTableId - Holds ID of the dealtable.
 */
export const onGroupApply = (
  rowData,
  discount,
  columnName,
  fieldMapIndex,
  dealTableId,
) => async dispatch => {
  const discountClone = cloneDeep(discount);
  const updatedDiscountData = generateGroupDataInDealTable(
    rowData,
    discountClone,
    columnName,
    fieldMapIndex,
  );

  const { groups } = updatedDiscountData;
  const newlyCreatedGroupsPayload = { groups };
  createDealTableGroup(dealTableId, newlyCreatedGroupsPayload).then(res => {
    for (let v = 0; v < res.Deal.groups.length; v += 1) {
      for (let d = 0; d < updatedDiscountData.groups.length; d += 1) {
        if (
          res.Deal.groups[v].group_by_value ===
          updatedDiscountData.groups[d].group_by_value
        ) {
          updatedDiscountData.groups[d].id = res.Deal.groups[v].id;
        }
      }
    }
    rowData.forEach(row => {
      row.rowData[fieldMapIndex.dt_group_id] = {};
      updatedDiscountData.groups.forEach(group => {
        if (
          group.group_by_value ===
          row.rowData[fieldMapIndex[columnName]].cellData
        ) {
          row.rowData[fieldMapIndex.dt_group_id].cellData = group.id;
        }
      });
    });
  });

  store.dispatch(
    onDealDiscountUpdate(
      updatedDiscountData.deal_discount,
      updatedDiscountData,
      dealTableId,
    ),
  );
  dispatch({
    type: "REACTION_ON_ROW_UPDATE_IN_DEALTABLE_WITH_GROUP_ID",
    payload: {
      dealTableId,
      rowData,
    },
  });
};

/**
 * "onGroupDelete" func saves the row(s), group discount & deal discount to BackEnd.
 * @param {arrayOfObj} rowData - Holds the current row(s) information in dealtable.
 * @param {Object} discount - Holds the discount related information of the selected dealtable.
 * @param {number} dealTableId - Holds ID of the dealtable.
 * @param {Object} fieldMapIndex - Holds the column name => FLDXXX map.
 */
export const onGroupDelete = (
  rowData,
  discount,
  dealTableId,
  fieldMapIndex,
) => async dispatch => {
  try {
    await removeAllDealTableGroups(dealTableId);
    const discountClone = cloneDeep(discount);
    discountClone.groups = [];
    let netPrices = [];
    const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];
    netPrices = rowData.map(row => row.rowData[netPriceFld].cellData);
    const dealTotal = netPrices.reduce((a, b) => a + b, 0);
    discountClone.groupReference = "";
    const discountAmount = (dealTotal * discountClone.deal_discount) / 100;
    discountClone.dealTotal = dealTotal;
    discountClone.discountAmount = discountAmount;
    discountClone.discountedDealTotal =
      discountClone.dealTotal - discountAmount;
    delete discountClone.discountedGroupTotal;

    rowData.forEach(row => {
      delete row.rowData[fieldMapIndex.dt_group_id];
    });

    dispatch({
      type: "REACTION_ON_ALL_GROUPS_DELETE_IN_DEALTABLE",
      payload: {
        dealTableId,
        discount: discountClone,
        rowData,
      },
    });
    return discountClone;
  } catch (error) {
    console.error("DealTableActions => onGroupDelete", error);
    throw error;
  }
};

/**
 * "netPriceRounding" func rounds off the net amount based on the user selection say,
 * by decimal, by units, by 10's, etc.,
 * @param {string} roundingType - Holds the value in which method the rounding has to be done (decimal, units, 10's, etc).
 * @param {arrayOfObj} rowData - Holds the current row(s) information in dealtable.
 * @param {Object} fieldMapIndex - Holds the column name => FLDXXX map.
 * @param {Object} discount - Holds the discount related information of the selected dealtable.
 * @param {number} dealTableId - Holds ID of the dealtable.
 */
export const netPriceRounding = (
  roundingType,
  rowData,
  metaData,
  discount,
  dealTableId,
) => async dispatch => {
  /**
   * This method allows user to round off the net price value based on their prefference.
   *
   * The following steps have to done in sequence.
   * 1) Clone RowData & Discount values, so it can be mutated.
   * 2) Call a parser method to perform Business Logic involved for this action.
   * 3) Create a payload to update the "rowData" & "discount" values in redux.
   * 4) Finally call ag-grid refresh api to updated the UI, this is done coz redux will hold the new data but grid UI shows the old value
   *    due to deltaRowMode feature.
   * 5) Return "true" so that, it returns callback to the action initiator method, which then displays success msg & hide loader.
   */

  // * STEP 1:
  const discountClone = cloneDeep(discount);
  const netPriceFld = metaData.fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];
  const rowDataClone = rowData.map(items => items);

  // * STEP 2:
  const updatedData = AgGridV4.reactionsOnNetPriceRounding(
    rowDataClone,
    metaData,
    discountClone,
    netPriceFld,
    roundingType,
    dealTableId,
  );

  // * STEP 3:
  dispatch({
    type: "REACTION_ON_ROUNDING_NETAMOUNT_IN_DEALTABLE",
    payload: {
      dealTableId,
      discount: updatedData.discount,
      rowData: updatedData.rowData,
    },
  });
};

export const applySorting = (
  col,
  dealTableId,
  rows,
  columnDefs,
) => async dispatch => {
  let updatedRowData = [];
  const rowsInfo = JSON.parse(JSON.stringify(rows));
  const updatedCoulmnDefs = JSON.parse(JSON.stringify(columnDefs));
  updatedCoulmnDefs.forEach(data => {
    if (col.field === data.field && col.sortStatus === "") {
      data.sortStatus = "arrow-up";
    } else if (col.field === data.field && col.sortStatus === "arrow-up") {
      data.sortStatus = "arrow-down";
    } else {
      data.sortStatus = "";
    }
  });
  if (col.sortStatus === "") {
    if (
      col.type === "NUMBER_FIELD" ||
      col.type === "CURRENCY_FIELD" ||
      col.type === "OBJECT_FIELD" ||
      col.type === "PERCENTAGE_FIELD"
    ) {
      updatedRowData = rowsInfo.sort(
        (a, b) =>
          parseInt(a.rowData[col.field].cellData, 10) -
          parseInt(b.rowData[col.field].cellData, 10),
      );
    } else {
      updatedRowData = rowsInfo.sort((a, b) => {
        const nameA = a.rowData[col.field].cellData.toUpperCase(); // ignore upper and lowercase
        const nameB = b.rowData[col.field].cellData.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
    }
  } else {
    updatedRowData = rowsInfo.reverse();
  }
  dispatch({
    type: "REACTION_AFTER_ROW_SORTING_IN_DEALTABLE",
    payload: {
      dealTableId,
      rowData: updatedRowData,
      columnDefs: updatedCoulmnDefs,
    },
  });
};

export const applyColumnWidth = (
  dealTableId,
  changedColumns,
) => async dispatch => {
  dispatch({
    type: "APPLY_DEAL_TABLE_COLUM_WIDTH",
    payload: {
      dealTableId,
      changedColumns,
    },
  });
};



// WEBPACK FOOTER //
// ./src/actions/DealTableActions/DealTableActions.js